import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog, Box, Stack, Typography, Button, IconButton, TextField,
  Alert, Skeleton, CircularProgress, Divider, InputAdornment, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  CloseRounded as CloseIcon,
  ArrowBackRounded as BackIcon,
  PhoneAndroidRounded as PhoneIcon,
  CheckRounded as TickIcon,
  CheckCircleRounded as DoneIcon,
  ErrorOutlineRounded as FailIcon,
  LockRounded as LockIcon,
  BoltRounded as PayAllIcon,
} from '@mui/icons-material';
import API from '../BaseURL';
import { P, money, HERO_BG } from './portalTheme';

// ─────────────────────────────────────────────────────────────────
// Paying by mobile money — a checkout, not a form.
//
// WHY THIS IS SHAPED LIKE A CHECKOUT
//   The first version was three white boxes and a disabled button. It
//   was a *form*: it asked for input and gave nothing back. Payment
//   screens earn trust by always answering two questions on every
//   screen — what am I paying, and what happens when I press the
//   button — so the running total and the action live in a fixed
//   footer that never scrolls away, and the step rail across the top
//   says how much further there is to go.
//
// SETTLED IS A STATE, NOT AN ERROR
//   When a learner owes nothing, the old screen showed three empty
//   boxes and a dead button — a wall. Owing nothing is good news and
//   should read that way, and paying ahead is a thing parents
//   genuinely want to do before a term starts. So the settled case
//   gets its own panel and the inputs stay live.
//
// THE NETWORKS ARE RECOGNISABLE
//   A Ghanaian parent picks their wallet by its colour before they
//   read the word. Generic grey chips make them stop and read.
//
// THE WAITING STEP IS NOT A SPINNER
//   The prompt is on the handset and the parent has to go and look at
//   it. The screen says exactly that, shows the number it went to and
//   how long it has been waiting, and never claims success on its own
//   — the server re-asks the provider every poll.
//
// CLOSING MID-FLIGHT IS ALLOWED BUT NOT SILENT
//   Once the prompt is out, closing does not cancel the debit. Saying
//   so is the difference between a parent who checks their balance and
//   one who pays twice.
// ─────────────────────────────────────────────────────────────────

const POLL_MS = 2500;
const GIVE_UP_MS = 3 * 60 * 1000;

const STEPS = [
  { key: 'amount',  label: 'Amount' },
  { key: 'phone',   label: 'Wallet' },
  { key: 'confirm', label: 'Confirm' },
];

// The wallets by their own colours. A parent finds MTN by looking for
// yellow, not by reading three similar words.
const BRAND = {
  MTN:        { bg: '#FFCC00', fg: '#1A1A1A', short: 'MTN' },
  TELECEL:    { bg: '#E4002B', fg: '#FFFFFF', short: 'Telecel' },
  AIRTELTIGO: { bg: '#0A3D91', fg: '#FFFFFF', short: 'AT' },
};

const clean = (v) => String(v ?? '').replace(/[^\d.]/g, '');
const mmss = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

export default function PortalPayDialog({ open, onClose, onQueued, studentName }) {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));

  const [step, setStep] = useState('amount');
  const [opts, setOpts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [amounts, setAmounts] = useState({});
  const [phone, setPhone] = useState('');
  const [network, setNetwork] = useState('');

  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [elapsed, setElapsed] = useState(0);

  const pollRef = useRef(null);
  const tickRef = useRef(null);
  const startedAt = useRef(0);

  // ── Open ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setStep('amount'); setError(''); setStatus(null); setQuote(null);
    setAmounts({}); setElapsed(0); setLoading(true);
    API.get('/portal/pay/options')
      .then(r => {
        if (!alive) return;
        setOpts(r.data);
        setPhone(r.data?.phone || '');
        setNetwork(r.data?.phone_network || '');
      })
      .catch(e => { if (alive) setError(e?.response?.data?.message || 'Could not open the payment screen'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open]);

  const stopTimers = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
  }, []);
  useEffect(() => stopTimers, [stopTimers]);
  useEffect(() => { if (!open) stopTimers(); }, [open, stopTimers]);

  const accounts = opts?.accounts || [];
  const allocations = accounts
    .map(a => ({ account: a.account, amount: Number(clean(amounts[a.account]) || 0) }))
    .filter(a => a.amount > 0);
  const total = allocations.reduce((s, a) => s + a.amount, 0);
  const owedTotal = accounts.reduce((s, a) => s + Math.max(0, a.balance), 0);
  const allSettled = accounts.length > 0 && owedTotal <= 0;

  const tooSmall = total > 0 && opts?.min_amount > 0 && total < opts.min_amount;
  const tooBig   = total > 0 && opts?.max_amount > 0 && total > opts.max_amount;

  const payAll = () => {
    const next = {};
    for (const a of accounts) if (a.balance > 0) next[a.account] = a.balance.toFixed(2);
    setAmounts(next);
  };

  // ── Steps ─────────────────────────────────────────────────────
  const goConfirm = async () => {
    setError(''); setBusy(true);
    try {
      const r = await API.post('/portal/pay/quote', { allocations, phone });
      setQuote(r.data);
      if (!r.data.phone) { setError('That does not look like a Ghanaian mobile number.'); return; }
      if (r.data.network && !network) setNetwork(r.data.network);
      setStep('confirm');
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not price that payment');
    } finally { setBusy(false); }
  };

  const poll = useCallback((reference) => {
    stopTimers();
    tickRef.current = setInterval(() => setElapsed(Date.now() - startedAt.current), 1000);
    pollRef.current = setInterval(async () => {
      try {
        const r = await API.get(`/portal/pay/${encodeURIComponent(reference)}`);
        setStatus(r.data);
        if (['SUCCESS', 'FAILED', 'CANCELLED', 'EXPIRED'].includes(r.data.status)) {
          stopTimers();
          if (r.data.status === 'SUCCESS' && onQueued) onQueued(r.data);
        } else if (Date.now() - startedAt.current > GIVE_UP_MS) {
          // Stop asking, but do not claim it failed — the server stays
          // the authority and the parent may yet approve.
          stopTimers();
        }
      } catch { /* a dropped request is not an answer; keep polling */ }
    }, POLL_MS);
  }, [onQueued, stopTimers]);

  const send = async () => {
    setError(''); setBusy(true);
    try {
      const r = await API.post('/portal/pay', { allocations, phone, network });
      setStatus(r.data);
      setStep('waiting');
      startedAt.current = Date.now();
      setElapsed(0);
      poll(r.data.reference);
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not start the payment');
    } finally { setBusy(false); }
  };

  const terminal = status && ['SUCCESS', 'FAILED', 'CANCELLED', 'EXPIRED'].includes(status.status);
  const inFlight = step === 'waiting' && !terminal;
  const close = () => { stopTimers(); onClose(); };
  const restart = () => { setStatus(null); setQuote(null); setError(''); setElapsed(0); setStep('amount'); };

  const stepIndex = STEPS.findIndex(s => s.key === step);
  const disabled = !opts?.enabled;

  // ── Header ────────────────────────────────────────────────────
  const header = (
    <Box sx={{ backgroundImage: HERO_BG, px: { xs: 2.5, sm: 3 }, pt: 2.2, pb: 2 }}>
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        {stepIndex > 0 && step !== 'waiting' && (
          <IconButton size="small" onClick={() => setStep(STEPS[stepIndex - 1].key)}
            sx={{ color: P.onBrand, ml: -1, mt: -.4 }}>
            <BackIcon />
          </IconButton>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 19, fontWeight: 900, color: P.onBrand, lineHeight: 1.25 }}>
            {step === 'waiting'
              ? (terminal ? 'Payment' : 'Approve on your phone')
              : 'Pay school fees'}
          </Typography>
          <Typography noWrap sx={{ fontSize: 12.5, color: P.onBrandSoft, mt: .1 }}>
            {studentName || opts?.guardian || 'Mobile money'}
          </Typography>
        </Box>
        <IconButton size="small" onClick={close} disabled={inFlight}
          sx={{ color: P.onBrand, mr: -1, mt: -.4, opacity: inFlight ? .35 : 1 }}>
          <CloseIcon />
        </IconButton>
      </Stack>

      {/* The step rail. Segments rather than a label, so progress is
          visible at a glance instead of read. */}
      {step !== 'waiting' && (
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          {STEPS.map((s, i) => {
            const done = i < stepIndex;
            const now  = i === stepIndex;
            return (
              <Box key={s.key} sx={{ flex: 1 }}>
                <Box sx={{
                  height: 3, borderRadius: 2, mb: .7,
                  bgcolor: done || now ? P.onBrand : 'rgba(255,255,255,.28)',
                  transition: 'background-color .25s',
                }} />
                <Stack direction="row" alignItems="center" spacing={.5}>
                  {done && <TickIcon sx={{ fontSize: 12, color: P.onBrand }} />}
                  <Typography sx={{
                    fontSize: 10.5, fontWeight: 800, letterSpacing: .4,
                    textTransform: 'uppercase',
                    color: done || now ? P.onBrand : P.onBrandFaint,
                  }}>
                    {s.label}
                  </Typography>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );

  // ── Footer: the running total and the action, always in view ──
  const footer = (label, action, enabled, note) => (
    <Box sx={{
      px: { xs: 2.5, sm: 3 }, py: 2, bgcolor: P.card,
      borderTop: '1px solid', borderColor: P.line,
    }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 1.4 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontSize: 10.5, fontWeight: 800, letterSpacing: .5,
            textTransform: 'uppercase', color: P.inkSoft,
          }}>
            {step === 'confirm' ? 'You will be charged' : 'Total to pay'}
          </Typography>
          {note && (
            <Typography sx={{ fontSize: 11.5, color: P.inkSoft, mt: .1 }}>{note}</Typography>
          )}
        </Box>
        <Typography sx={{
          fontSize: 24, fontWeight: 900, lineHeight: 1.1,
          color: total > 0 ? P.ink : P.inkSoft, fontVariantNumeric: 'tabular-nums',
        }}>
          <Box component="span" sx={{ fontSize: 13, fontWeight: 800, color: P.inkSoft, mr: .6 }}>
            GHS
          </Box>
          {money(step === 'confirm' && quote ? quote.charged : total)}
        </Typography>
      </Stack>
      <Button fullWidth variant="contained" size="large" disabled={!enabled || busy}
        onClick={action}
        sx={{
          fontWeight: 800, fontSize: 15, borderRadius: 2.5, py: 1.35,
          textTransform: 'none', bgcolor: P.brand, boxShadow: 'none',
          '&:hover': { bgcolor: P.brandDeep, boxShadow: 'none' },
        }}>
        {busy ? <CircularProgress size={23} color="inherit" /> : label}
      </Button>
      <Stack direction="row" justifyContent="center" alignItems="center" spacing={.6} sx={{ mt: 1.2 }}>
        <LockIcon sx={{ fontSize: 12, color: P.inkSoft }} />
        <Typography sx={{ fontSize: 11, color: P.inkSoft }}>
          You approve every debit on your own handset
        </Typography>
      </Stack>
    </Box>
  );

  const body = { px: { xs: 2.5, sm: 3 }, py: 2.5, flex: 1, overflowY: 'auto', bgcolor: P.bg };

  return (
    <Dialog open={open} onClose={inFlight ? undefined : close}
      maxWidth="sm" fullWidth fullScreen={isPhone}
      PaperProps={{ sx: {
        borderRadius: isPhone ? 0 : 4, overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        height: isPhone ? '100%' : 'auto', maxHeight: isPhone ? '100%' : '92vh',
      } }}>
      {header}

      {loading ? (
        <Box sx={body}>
          <Stack spacing={1.4}>
            {[0, 1, 2].map(i => <Skeleton key={i} variant="rounded" height={62} sx={{ borderRadius: 2.5 }} />)}
          </Stack>
        </Box>
      ) : disabled ? (
        <>
          <Box sx={body}>
            <Alert severity="info" sx={{ borderRadius: 2.5 }}>
              Online payment is not switched on for this school yet. Please pay at the
              school office — your balances here stay up to date either way.
            </Alert>
          </Box>
          <Box sx={{ px: 3, py: 2, bgcolor: P.card, borderTop: '1px solid', borderColor: P.line }}>
            <Button fullWidth variant="contained" onClick={close}
              sx={{ fontWeight: 800, borderRadius: 2.5, py: 1.2, textTransform: 'none',
                bgcolor: P.brand, boxShadow: 'none', '&:hover': { bgcolor: P.brandDeep } }}>
              Close
            </Button>
          </Box>
        </>
      ) : (
        <>
          {/* ── 1. Amount ──────────────────────────────────────── */}
          {step === 'amount' && (
            <>
              <Box sx={body}>
                {error && <Alert severity="error" sx={{ borderRadius: 2.5, mb: 2 }}>{error}</Alert>}

                {/* Owing nothing is good news, and the old screen made
                    it look like a fault. */}
                {allSettled ? (
                  <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{
                    p: 1.8, mb: 2, borderRadius: 2.5,
                    bgcolor: '#E9F7EE', border: '1px solid #C7E9D3',
                  }}>
                    <DoneIcon sx={{ color: P.good, fontSize: 22 }} />
                    <Box>
                      <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#14532D' }}>
                        Every account is settled
                      </Typography>
                      <Typography sx={{ fontSize: 12.5, color: '#2E6B45', mt: .2 }}>
                        Nothing is owed right now. You can still pay ahead — enter an amount
                        below and it will sit on the account as credit.
                      </Typography>
                    </Box>
                  </Stack>
                ) : (
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.8 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 800, color: P.ink }}>
                        GHS {money(owedTotal)} outstanding
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: P.inkSoft }}>
                        Pay some or all of it.
                      </Typography>
                    </Box>
                    <Button size="small" startIcon={<PayAllIcon sx={{ fontSize: 16 }} />}
                      onClick={payAll} variant="outlined"
                      sx={{
                        fontWeight: 800, textTransform: 'none', borderRadius: 5,
                        color: P.brandDeep, borderColor: P.brand, whiteSpace: 'nowrap',
                        '&:hover': { borderColor: P.brandDeep, bgcolor: P.brandFaint },
                      }}>
                      Pay all
                    </Button>
                  </Stack>
                )}

                {/* Dense rows, not big empty cards. A row that has an
                    amount in it lights up, so the selection is visible
                    without reading the numbers back. */}
                <Stack spacing={1}>
                  {accounts.map(a => {
                    const v = Number(clean(amounts[a.account]) || 0);
                    const on = v > 0;
                    return (
                      <Stack key={a.account} direction="row" alignItems="center" spacing={1.5}
                        sx={{
                          px: 1.8, py: 1.5, borderRadius: 2.5, bgcolor: P.card,
                          border: '1.5px solid',
                          borderColor: on ? P.brand : P.line,
                          boxShadow: on ? `0 0 0 3px ${P.brandSoft}` : 'none',
                          transition: 'border-color .18s, box-shadow .18s',
                        }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography noWrap sx={{ fontSize: 14.5, fontWeight: 800, color: P.ink }}>
                            {a.label}
                          </Typography>
                          <Typography sx={{
                            fontSize: 12, mt: .1,
                            color: a.balance > 0 ? P.bad : a.balance < 0 ? P.good : P.inkSoft,
                            fontWeight: a.balance === 0 ? 500 : 700,
                          }}>
                            {a.balance > 0 ? `GHS ${money(a.balance)} owing`
                              : a.balance < 0 ? `GHS ${money(-a.balance)} in credit`
                              : 'Settled'}
                          </Typography>
                        </Box>
                        <TextField
                          size="small" placeholder="0.00"
                          value={amounts[a.account] ?? ''}
                          onChange={e => setAmounts(m => ({ ...m, [a.account]: clean(e.target.value) }))}
                          inputProps={{
                            inputMode: 'decimal',
                            style: { textAlign: 'right', fontWeight: 800, fontSize: 15 },
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start" sx={{ mr: .5 }}>
                                <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: P.inkSoft }}>
                                  GHS
                                </Typography>
                              </InputAdornment>
                            ),
                            sx: { borderRadius: 2, bgcolor: on ? P.brandFaint : 'transparent' },
                          }}
                          sx={{ width: 138 }}
                        />
                      </Stack>
                    );
                  })}
                </Stack>

                {(tooSmall || tooBig) && (
                  <Alert severity="warning" sx={{ borderRadius: 2.5, mt: 2 }}>
                    {tooSmall
                      ? `The smallest payment is GHS ${money(opts.min_amount)}.`
                      : `The largest single payment is GHS ${money(opts.max_amount)}. Pay in `
                        + `instalments, or settle the rest at the school office.`}
                  </Alert>
                )}
              </Box>
              {footer('Continue', () => { setError(''); setStep('phone'); },
                total > 0 && !tooSmall && !tooBig,
                allocations.length
                  ? `${allocations.length} account${allocations.length === 1 ? '' : 's'}`
                  : 'Enter an amount to continue')}
            </>
          )}

          {/* ── 2. Wallet ──────────────────────────────────────── */}
          {step === 'phone' && (
            <>
              <Box sx={body}>
                {error && <Alert severity="error" sx={{ borderRadius: 2.5, mb: 2 }}>{error}</Alert>}

                <Typography sx={{
                  fontSize: 10.5, fontWeight: 800, letterSpacing: .5,
                  textTransform: 'uppercase', color: P.inkSoft, mb: 1,
                }}>
                  Choose your wallet
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
                  {(opts?.networks || []).map(n => {
                    const b = BRAND[n.key] || { bg: P.brand, fg: '#fff', short: n.key };
                    const on = network === n.key;
                    return (
                      <Box key={n.key} onClick={() => setNetwork(n.key)}
                        sx={{
                          p: 1.4, borderRadius: 2.5, cursor: 'pointer', textAlign: 'center',
                          bgcolor: P.card, border: '1.5px solid',
                          borderColor: on ? P.brand : P.line,
                          boxShadow: on ? `0 0 0 3px ${P.brandSoft}` : 'none',
                          transition: 'border-color .18s, box-shadow .18s',
                        }}>
                        <Box sx={{
                          width: 40, height: 40, borderRadius: 2, mx: 'auto', mb: .8,
                          bgcolor: b.bg, color: b.fg,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11.5, fontWeight: 900, letterSpacing: .3,
                        }}>
                          {b.short}
                        </Box>
                        <Typography sx={{
                          fontSize: 11.5, fontWeight: 800, lineHeight: 1.25,
                          color: on ? P.brandDeep : P.ink,
                        }}>
                          {n.label}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>

                <Typography sx={{
                  fontSize: 10.5, fontWeight: 800, letterSpacing: .5,
                  textTransform: 'uppercase', color: P.inkSoft, mt: 2.5, mb: 1,
                }}>
                  Mobile money number
                </Typography>
                <TextField
                  fullWidth value={phone}
                  onChange={e => { setPhone(e.target.value); setNetwork(''); }}
                  placeholder="024 000 0000"
                  inputProps={{ inputMode: 'tel', style: { fontWeight: 800, fontSize: 17 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon sx={{ fontSize: 19, color: P.inkSoft }} />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 2.5, bgcolor: P.card },
                  }}
                />
                <Typography sx={{ fontSize: 12, color: P.inkSoft, mt: 1 }}>
                  {opts?.phone && phone === opts.phone
                    ? 'This is the number the school has on file. You can change it — the '
                      + 'wallet paying does not have to be the registered one.'
                    : 'The approval prompt goes to this number.'}
                </Typography>
              </Box>
              {footer('Continue', goConfirm, !!phone && !!network,
                network ? (opts?.networks || []).find(n => n.key === network)?.label : 'Pick a wallet')}
            </>
          )}

          {/* ── 3. Confirm ─────────────────────────────────────── */}
          {step === 'confirm' && quote && (
            <>
              <Box sx={body}>
                {error && <Alert severity="error" sx={{ borderRadius: 2.5, mb: 2 }}>{error}</Alert>}

                <Box sx={{
                  borderRadius: 3, bgcolor: P.card, p: 2.2,
                  border: '1px solid', borderColor: P.line,
                }}>
                  <Typography sx={{
                    fontSize: 10.5, fontWeight: 800, letterSpacing: .5,
                    textTransform: 'uppercase', color: P.inkSoft, mb: 1.4,
                  }}>
                    Paying
                  </Typography>
                  {quote.lines.map(l => (
                    <Stack key={l.account} direction="row" sx={{ py: .55 }}>
                      <Typography sx={{ flex: 1, fontSize: 13.5, color: P.ink }}>
                        {String(l.account).replace(/_+$/, '').replace(/_/g, ' ')}
                      </Typography>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 700,
                        fontVariantNumeric: 'tabular-nums', color: P.ink }}>
                        {money(l.amount)}
                      </Typography>
                    </Stack>
                  ))}

                  <Divider sx={{ my: 1.3 }} />
                  <Stack direction="row" sx={{ py: .3 }}>
                    <Typography sx={{ flex: 1, fontSize: 13, color: P.inkSoft }}>Subtotal</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: P.inkSoft,
                      fontVariantNumeric: 'tabular-nums' }}>
                      {money(quote.total)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" sx={{ py: .3 }}>
                    <Typography sx={{ flex: 1, fontSize: 13, color: P.inkSoft }}>
                      Transaction fee
                    </Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: P.inkSoft,
                      fontVariantNumeric: 'tabular-nums' }}>
                      {quote.fee_mode === 'school' ? 'paid by the school' : money(quote.fee)}
                    </Typography>
                  </Stack>

                  <Divider sx={{ my: 1.3 }} />
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{
                    px: 1.4, py: 1.2, borderRadius: 2, bgcolor: P.brandFaint,
                  }}>
                    <Box sx={{
                      width: 34, height: 34, borderRadius: 1.5,
                      bgcolor: (BRAND[network] || {}).bg || P.brand,
                      color: (BRAND[network] || {}).fg || '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 10, fontWeight: 900,
                    }}>
                      {(BRAND[network] || {}).short || network}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: P.ink }}>
                        {quote.phone}
                      </Typography>
                      <Typography sx={{ fontSize: 11.5, color: P.inkSoft }}>
                        {(opts?.networks || []).find(n => n.key === network)?.label || network}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                {/* Overpaying is allowed, but it should be said out loud
                    rather than discovered on a statement. */}
                {quote.overpay?.length > 0 && (
                  <Alert severity="info" sx={{ borderRadius: 2.5, mt: 2 }}>
                    This is more than is owed on{' '}
                    {quote.overpay.map(o => String(o.account).replace(/_/g, ' ')).join(', ')}.
                    The extra stays on the account as credit.
                  </Alert>
                )}
              </Box>
              {footer(`Send prompt to ${quote.phone}`, send, true,
                quote.fee_mode === 'school'
                  ? 'The school covers the fee'
                  : `Includes GHS ${money(quote.fee)} fee`)}
            </>
          )}

          {/* ── 4. Waiting / result ────────────────────────────── */}
          {step === 'waiting' && status && (
            <>
              <Box sx={{ ...body, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {!terminal ? (
                  <Stack spacing={2} alignItems="center" sx={{ py: 2, maxWidth: 360 }}>
                    <Box sx={{ position: 'relative', display: 'flex' }}>
                      <CircularProgress size={78} thickness={2.5} sx={{ color: P.brand }} />
                      <Box sx={{
                        position: 'absolute', inset: 0, display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                      }}>
                        <PhoneIcon sx={{ fontSize: 30, color: P.brandDeep }} />
                      </Box>
                    </Box>
                    <Typography sx={{ fontSize: 26, fontWeight: 900, color: P.ink,
                      fontVariantNumeric: 'tabular-nums' }}>
                      GHS {money(status.charged)}
                    </Typography>
                    <Typography sx={{ fontSize: 15, fontWeight: 800, color: P.ink, textAlign: 'center' }}>
                      Check {status.phone}
                    </Typography>
                    <Typography sx={{ fontSize: 13.5, color: P.inkSoft, textAlign: 'center', lineHeight: 1.5 }}>
                      A prompt has been sent to your phone. Enter your mobile money PIN to
                      approve it — this usually takes under a minute.
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: P.inkSoft, fontVariantNumeric: 'tabular-nums' }}>
                      Waiting {mmss(elapsed)}
                    </Typography>
                  </Stack>
                ) : status.status === 'SUCCESS' ? (
                  <Stack spacing={1.4} alignItems="center" sx={{ py: 1, width: '100%', maxWidth: 380 }}>
                    <Box sx={{
                      width: 66, height: 66, borderRadius: '50%', bgcolor: '#E9F7EE',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <DoneIcon sx={{ fontSize: 40, color: P.good }} />
                    </Box>
                    <Typography sx={{ fontSize: 18, fontWeight: 900, color: P.ink }}>
                      Payment received
                    </Typography>
                    <Typography sx={{ fontSize: 24, fontWeight: 900, color: P.ink,
                      fontVariantNumeric: 'tabular-nums' }}>
                      GHS {money(status.charged)}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: P.inkSoft, textAlign: 'center', lineHeight: 1.5 }}>
                      Debited from {status.phone}. The school will confirm it and issue your
                      receipt shortly — it will appear under Receipts.
                    </Typography>
                    <Box sx={{
                      width: '100%', mt: .6, borderRadius: 2.5, p: 1.6, bgcolor: P.card,
                      border: '1px solid', borderColor: P.line,
                    }}>
                      {status.lines.map(l => (
                        <Stack key={l.account} direction="row" alignItems="center" sx={{ py: .45 }}>
                          <Typography noWrap sx={{ flex: 1, fontSize: 12.5, color: P.ink }}>
                            {String(l.account).replace(/_+$/, '').replace(/_/g, ' ')}
                          </Typography>
                          <Typography sx={{
                            fontSize: 10.5, fontWeight: 800, mr: 1.2, px: .9, py: .25,
                            borderRadius: 5,
                            bgcolor: l.approval_status === 'APPROVED' ? '#E9F7EE' : P.brandSoft,
                            color: l.approval_status === 'APPROVED' ? P.good : P.brandDeep,
                          }}>
                            {l.approval_status === 'APPROVED' ? 'Confirmed' : 'Awaiting school'}
                          </Typography>
                          <Typography sx={{ fontSize: 12.5, fontWeight: 800,
                            fontVariantNumeric: 'tabular-nums' }}>
                            {money(l.amount)}
                          </Typography>
                        </Stack>
                      ))}
                    </Box>
                    <Typography sx={{ fontSize: 10.5, color: P.inkSoft, fontFamily: 'monospace', mt: .4 }}>
                      {status.reference}
                    </Typography>
                  </Stack>
                ) : (
                  <Stack spacing={1.4} alignItems="center" sx={{ py: 1, maxWidth: 360 }}>
                    <Box sx={{
                      width: 66, height: 66, borderRadius: '50%', bgcolor: '#FDECEA',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <FailIcon sx={{ fontSize: 40, color: P.bad }} />
                    </Box>
                    <Typography sx={{ fontSize: 18, fontWeight: 900, color: P.ink, textAlign: 'center' }}>
                      {status.status === 'EXPIRED' ? 'The prompt timed out' : 'Payment not completed'}
                    </Typography>
                    <Typography sx={{ fontSize: 13.5, color: P.inkSoft, textAlign: 'center', lineHeight: 1.5 }}>
                      {status.message}
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: P.inkSoft, textAlign: 'center' }}>
                      Nothing was taken from your wallet and your balance has not changed.
                    </Typography>
                  </Stack>
                )}
              </Box>

              <Box sx={{
                px: { xs: 2.5, sm: 3 }, py: 2, bgcolor: P.card,
                borderTop: '1px solid', borderColor: P.line,
              }}>
                {!terminal ? (
                  <>
                    {/* Closing does not cancel the debit, and pretending
                        otherwise is how a parent pays twice. */}
                    <Typography sx={{ fontSize: 12, color: P.inkSoft, textAlign: 'center', mb: 1.2 }}>
                      Closing this will not cancel the prompt. Check your balance here before
                      trying again.
                    </Typography>
                    <Button fullWidth variant="outlined" onClick={close}
                      sx={{ fontWeight: 800, borderRadius: 2.5, py: 1.2, textTransform: 'none',
                        color: P.inkSoft, borderColor: P.line }}>
                      Close and check later
                    </Button>
                  </>
                ) : status.status === 'SUCCESS' ? (
                  <Button fullWidth variant="contained" onClick={close}
                    sx={{ fontWeight: 800, fontSize: 15, borderRadius: 2.5, py: 1.35,
                      textTransform: 'none', bgcolor: P.brand, boxShadow: 'none',
                      '&:hover': { bgcolor: P.brandDeep, boxShadow: 'none' } }}>
                    Done
                  </Button>
                ) : (
                  <Stack direction="row" spacing={1.2}>
                    <Button fullWidth variant="outlined" onClick={close}
                      sx={{ fontWeight: 800, borderRadius: 2.5, py: 1.2, textTransform: 'none',
                        color: P.inkSoft, borderColor: P.line }}>
                      Close
                    </Button>
                    <Button fullWidth variant="contained" onClick={restart}
                      sx={{ fontWeight: 800, borderRadius: 2.5, py: 1.2, textTransform: 'none',
                        bgcolor: P.brand, boxShadow: 'none',
                        '&:hover': { bgcolor: P.brandDeep, boxShadow: 'none' } }}>
                      Try again
                    </Button>
                  </Stack>
                )}
              </Box>
            </>
          )}
        </>
      )}
    </Dialog>
  );
}
